import {DEFAULT_OPTION_FONT_SIZE, DEFAULT_OPTION_LINE_HEIGHT} from '../../lib/constants';
import type {TextAlign, TextMarkStyle} from '../../lib/rich-text';
import type {PhotoRequirement} from '../../lib/types';

/**
 * How an element's authored style becomes CSS.
 *
 * Every renderer starts here, and the layering order is the whole subject:
 * a built-in default at the bottom, the element's own style over it, and — for
 * an element in a table cell — the cell's type over that, via `overlayStyle`.
 */

export function buildTextStyle(
  s: PhotoRequirement['style'],
  defaults?: {fontSize?: string; fontWeight?: string; color?: string},
): React.CSSProperties {
  return {
    fontSize: s.font_size ? `${s.font_size}px` : defaults?.fontSize,
    fontWeight: s.font_weight ?? defaults?.fontWeight,
    fontStyle: s.font_style ?? undefined,
    textDecoration: s.text_decoration ?? undefined,
    color: s.color ?? defaults?.color,
    textAlign: (s.text_align as React.CSSProperties['textAlign']) ?? undefined,
  };
}

/** A mark's own styles as CSS, for a span inside the element's text. */
export function markCss(style: TextMarkStyle | undefined): React.CSSProperties | undefined {
  if (!style) return undefined;
  const css: React.CSSProperties = {};
  if (style.bold !== undefined) css.fontWeight = style.bold ? 'bold' : 'normal';
  if (style.italic !== undefined) css.fontStyle = style.italic ? 'italic' : 'normal';
  if (style.underline !== undefined) css.textDecoration = style.underline ? 'underline' : 'none';
  if (style.color !== undefined) css.color = style.color;
  if (style.font_size) css.fontSize = `${style.font_size}px`;
  return css;
}

/** A line of text that carries its own alignment, as a block of its own. A
 *  line with none inherits the element's, like the text around it. */
export function lineCss(align: TextAlign | undefined): React.CSSProperties {
  return align ? {display: 'block', textAlign: align} : {display: 'block'};
}

export function buildBaseStyle(s: PhotoRequirement['style']): React.CSSProperties {
  return {
    ...buildTextStyle(s),
    backgroundColor: s.background_color ?? undefined,
    borderWidth: s.border_width ? `${s.border_width}px` : undefined,
    borderStyle: s.border_width ? (s.border_style ?? 'solid') : undefined,
    borderColor: s.border_color ?? undefined,
    borderRadius: s.border_radius ? `${s.border_radius}px` : undefined,
    padding: s.padding ?? undefined,
    opacity: s.opacity ?? undefined,
  };
}

/**
 * `over` layered on `base`, ignoring every key `over` leaves undefined.
 *
 * A plain spread cannot do this: `{...base, ...over}` writes `undefined` over a
 * real value, so a cell that sets no font size would have erased the option
 * row's own default rather than leaving it alone. Only what the author actually
 * set should win.
 */
export function overlayStyle(
  base: React.CSSProperties,
  over?: React.CSSProperties,
): React.CSSProperties {
  if (!over) return base;
  const out: Record<string, unknown> = {...base};
  for (const [k, v] of Object.entries(over)) {
    if (v !== undefined) out[k] = v;
  }
  return out as React.CSSProperties;
}

/**
 * The typography one option row renders in.
 *
 * Three layers, and the order is the whole of it: the toolbar's defaults at the
 * bottom, the element's own style over them, and a cell's type over that —
 * `overlayStyle`, so a cell that sets nothing erases nothing.
 *
 * A checkbox and a dropdown answer this identically, and used to answer it in
 * two copies of the same eight lines. Sitting them in one file made that easy to
 * see and no less real: what stops the two drifting is sharing the rule, not
 * sharing a filename.
 */
export function optionRowStyle(
  s: PhotoRequirement['style'],
  textStyle?: React.CSSProperties,
): React.CSSProperties {
  return overlayStyle(
    {
      ...buildTextStyle(s, {fontSize: `${DEFAULT_OPTION_FONT_SIZE}px`}),
      lineHeight: DEFAULT_OPTION_LINE_HEIGHT,
    },
    textStyle,
  );
}
